/**
 * Rebar Quantity Synchronization
 *
 * This script automatically syncs the quantity of "Add Threaded Rebar" items
 * with their linked product quantities in the cart.
 */

class RebarSync {
  constructor() {
    this.cartUpdateUnsubscriber = undefined;
    this.init();
  }

  init() {
    // Subscribe to cart update events
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Skip if this sync triggered the update to avoid infinite loops
      if (event.source === 'rebar-sync') {
        return;
      }

      console.log('🔄 RebarSync: Cart updated, checking for rebar sync needs');
      this.syncRebar(event.cartData);
    });

    // Also run initial sync on page load
    this.syncRebarOnLoad();
  }

  /**
   * Initial sync when page loads - gets cart data via fetch
   */
  async syncRebarOnLoad() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cartData = await response.json();
      console.log('🔄 RebarSync: Initial page load sync');
      this.syncRebar(cartData);
    } catch (error) {
      console.error('🚨 RebarSync: Failed to fetch cart data on load:', error);
    }
  }

  /**
   * Main sync logic - handles rebar addon cleanup only
   *
   * INDEPENDENT QTY MODEL:
   * - Rebar quantity is chosen by the customer via a qty selector (0-10)
   * - This sync only removes the rebar addon when NO parent products with rebar remain
   * - It does NOT adjust rebar qty to match parent count (qty is user-controlled)
   */
  syncRebar(cartData) {
    if (!cartData || !cartData.items) {
      console.log('🔄 RebarSync: No cart data available');
      return;
    }

    console.log('🔄 RebarSync: Analyzing cart:', cartData.items.length, 'items');

    // Find rebar addon and sum Rebar Qty from all parent products
    let rebarItem = null;
    let rebarLineNumber = 0;
    let totalRebarNeeded = 0;

    cartData.items.forEach((item, index) => {
      const lineNumber = index + 1;

      if (this.isRebarItem(item)) {
        // Found rebar addon
        if (!rebarItem) {
          rebarItem = item;
          rebarLineNumber = lineNumber;
          console.log(`🔩 Found rebar addon: ${item.product_title} (line ${lineNumber}, qty: ${item.quantity})`);
        } else {
          console.warn(`⚠️ Multiple rebar items found! Line ${lineNumber} should be consolidated.`);
        }
      } else if (item.properties && item.properties['Add Threaded Rebar'] === 'Yes') {
        // Sum up Rebar Qty from each parent product
        const perProductQty = parseInt(item.properties['Rebar Qty']) || item.quantity;
        totalRebarNeeded += perProductQty;
        console.log(`🔧 Product with rebar: ${item.product_title} (qty: ${item.quantity}, rebar_qty: ${perProductQty})`);
      }
    });

    console.log(`📊 RebarSync: Total rebar needed: ${totalRebarNeeded}`);

    const syncUpdates = [];

    // Case 1: Rebar addon exists but no products with rebar - remove it
    if (rebarItem && totalRebarNeeded === 0) {
      console.log(`🗑️ RebarSync: No products with rebar, removing rebar addon`);
      syncUpdates.push({
        line: rebarLineNumber,
        quantity: 0,
        reason: 'No products with rebar in cart'
      });
    }
    // Case 2: Rebar addon exists and qty doesn't match sum of Rebar Qty - update it
    else if (rebarItem && rebarItem.quantity !== totalRebarNeeded) {
      console.log(`🔄 RebarSync: Quantity mismatch - rebar: ${rebarItem.quantity}, needed: ${totalRebarNeeded}`);
      syncUpdates.push({
        line: rebarLineNumber,
        quantity: totalRebarNeeded,
        reason: `Syncing with sum of Rebar Qty across parents`
      });
    }
    // Case 3: Quantities match - no update needed
    else if (rebarItem) {
      console.log(`✅ RebarSync: Quantities match (${rebarItem.quantity})`);
    }
    // Case 4: No rebar addon but products with rebar exist
    else if (totalRebarNeeded > 0) {
      console.warn(`⚠️ RebarSync: Products with rebar exist but no rebar addon found!`);
    }

    // Apply sync updates
    if (syncUpdates.length > 0) {
      console.log('🔄 RebarSync: Applying updates:', syncUpdates);
      this.applySyncUpdates(syncUpdates);
    } else {
      console.log('✅ RebarSync: No sync updates needed');
    }
  }

  /**
   * Check if an item is a rebar addon
   */
  isRebarItem(item) {
    return item.sku === 'REBAR' ||
           (item.properties && item.properties.addon_type === 'rebar');
  }


  /**
   * Apply quantity sync updates to cart
   */
  async applySyncUpdates(updates) {
    for (const update of updates) {
      console.log(`🔄 RebarSync: Updating line ${update.line} to quantity ${update.quantity} - ${update.reason}`);

      try {
        const sectionsToUpdate = ['main-cart-items', 'cart-icon-bubble', 'cart-live-region-text', 'main-cart-footer'].filter(section => {
          const element = document.querySelector(`[data-id="${section}"], #${section}`);
          console.log(`🔍 RebarSync: Checking section "${section}": ${element ? 'FOUND' : 'NOT FOUND'}`);
          return element;
        });

        console.log(`📤 RebarSync: Requesting updates for sections:`, sectionsToUpdate);

        const body = JSON.stringify({
          line: update.line,
          quantity: update.quantity,
          sections: sectionsToUpdate,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: body
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.text();
        const parsedResult = JSON.parse(result);

        if (parsedResult.errors) {
          console.error('🚨 RebarSync: Cart update failed:', parsedResult.errors);
          continue;
        }

        console.log(`✅ RebarSync: Successfully updated line ${update.line}`);
        console.log(`📥 RebarSync: API response sections:`, Object.keys(parsedResult.sections || {}));

        // Check if main-cart-footer section was returned
        if (parsedResult.sections && parsedResult.sections['main-cart-footer']) {
          console.log(`💰 RebarSync: Cart footer section was updated`);
          // Manually recalculate wholesale total using updated cart data
          this.updateWholesaleTotal(parsedResult);
        } else {
          console.log(`⚠️ RebarSync: Cart footer section was NOT returned in response`);
        }

        // Publish cart update event so other components can react, but mark it as from our sync
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'rebar-sync',
          cartData: parsedResult
        });

      } catch (error) {
        console.error(`🚨 RebarSync: Failed to update line ${update.line}:`, error);
      }

      // Small delay between updates to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Update the wholesale total display using JavaScript
   */
  updateWholesaleTotal(cartData) {
    try {
      // Check if customer is wholesale
      if (!window.customer || !window.customer.tags || !window.customer.tags.includes('wholesale')) {
        console.log('💰 RebarSync: Not wholesale customer, skipping total update');
        return;
      }

      let wholesaleTotal = 0;

      // Calculate wholesale total using same logic as Liquid template
      cartData.items.forEach(item => {
        // Check if item has disc_price by looking at the cart item element
        const cartItemElement = document.getElementById(`CartItem-${cartData.items.indexOf(item) + 1}`);
        if (cartItemElement && cartItemElement.dataset.discPrice) {
          // Use wholesale price
          const discPrice = parseFloat(cartItemElement.dataset.discPrice) * 100; // Convert to cents
          const itemTotal = discPrice * item.quantity;
          wholesaleTotal += itemTotal;
          console.log(`💰 Using wholesale price for ${item.product_title}: $${discPrice/100} × ${item.quantity} = $${itemTotal/100}`);
        } else {
          // Use retail price as fallback
          wholesaleTotal += item.final_line_price;
          console.log(`💰 Using retail price for ${item.product_title}: $${item.final_line_price/100}`);
        }
      });

      console.log(`💰 RebarSync: Calculated wholesale total: $${wholesaleTotal/100}`);

      // Update the total display
      const totalValueElement = document.querySelector('.totals__total-value');
      if (totalValueElement) {
        // Format the price (assuming USD)
        const formattedPrice = (wholesaleTotal / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        });
        totalValueElement.textContent = formattedPrice;
        console.log(`💰 RebarSync: Updated total display to ${formattedPrice}`);
      } else {
        console.log('⚠️ RebarSync: Could not find total display element');
      }

    } catch (error) {
      console.error('🚨 RebarSync: Error updating wholesale total:', error);
    }
  }

  /**
   * Manual sync function for debugging - can be called from browser console
   */
  async testSync() {
    console.log('🧪 RebarSync: Manual test sync initiated');
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cartData = await response.json();
      console.log('🧪 Current cart data:', cartData);
      this.syncRebar(cartData);
    } catch (error) {
      console.error('🚨 RebarSync: Test sync failed:', error);
    }
  }

  /**
   * Cleanup when component is destroyed
   */
  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

// Initialize rebar sync when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('🔄 RebarSync: DOM loaded, checking dependencies...');

  // Check if required dependencies are available
  if (typeof PUB_SUB_EVENTS === 'undefined') {
    console.error('🚨 RebarSync: PUB_SUB_EVENTS not defined - constants.js not loaded yet');
    return;
  }

  if (typeof subscribe === 'undefined') {
    console.error('🚨 RebarSync: subscribe not defined - pubsub.js not loaded yet');
    return;
  }

  if (typeof fetchConfig === 'undefined') {
    console.error('🚨 RebarSync: fetchConfig not defined - global.js not loaded yet');
    return;
  }

  if (typeof routes === 'undefined') {
    console.error('🚨 RebarSync: routes not defined');
    return;
  }

  console.log('🔄 RebarSync: All dependencies available, initializing...');
  window.rebarSync = new RebarSync();
});

// Also try to initialize on window load as fallback
window.addEventListener('load', () => {
  if (!window.rebarSync) {
    console.log('🔄 RebarSync: Fallback initialization on window load');
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe !== 'undefined' && typeof fetchConfig !== 'undefined') {
      window.rebarSync = new RebarSync();
    } else {
      console.error('🚨 RebarSync: Dependencies still not available on window load');
    }
  }
});
